import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../../config";
import "../../styles/TermsGate.css";

// Shown when a signed-in account accepted an older version of the terms than
// the one published now.
//
// --- why login still succeeds ---------------------------------------------
//
// Refusing to log in would mean issuing no token, and with no token there is
// nothing to authenticate the acceptance request with. The account would be
// locked out by the very prompt meant to let it back in. So the token is
// issued, the login response carries `terms_current: false`, and this gates
// the UI instead.
//
// That records acceptance reliably and puts the document in front of the
// person, which is what the obligation actually is. It is NOT an API-level
// block, and the route comment in auth_routes.py says the same thing --
// anything that genuinely must not happen on stale terms has to check
// server-side.
//
// --- why the state is fetched, not read from localStorage -----------------
//
// The terms can be republished while somebody is signed in. Asking
// /api/auth/me on mount means a session that was current an hour ago is
// re-prompted, rather than staying quietly stale until the token expires.

export default function TermsGate({ children }) {
    const [state, setState] = useState({ status: "checking" });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const token = localStorage.getItem("token");

    const check = useCallback(async () => {
        if (!token) {
            setState({ status: "ok" });
            return;
        }
        try {
            const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) {
                // Expired or rejected token. Not this component's problem --
                // App.js already handles sign-out, and blocking the whole app
                // behind a failed check would be worse than letting it render.
                setState({ status: "ok" });
                return;
            }
            const data = await res.json();
            if (data.terms_current === false) {
                setState({ status: "stale", version: data.terms_version_required });
            } else {
                setState({ status: "ok" });
            }
        } catch {
            // Offline or the API is down. Fails OPEN, deliberately: the
            // opposite of the consent banner. A network blip must not lock
            // someone out of an account holding their earnings, and the prompt
            // reappears on the next successful check.
            setState({ status: "ok" });
        }
    }, [token]);

    useEffect(() => { check(); }, [check]);

    async function accept() {
        setBusy(true);
        setError("");
        try {
            const res = await fetch(`${API_BASE_URL}/api/auth/accept-terms`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                // Names the version that was on screen. If the documents were
                // republished while this was open the server answers 409
                // rather than recording agreement to text nobody saw.
                body: JSON.stringify({ terms_version: state.version }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.message || "That did not work. Please try again.");
                if (res.status === 409) check();
                return;
            }
            setState({ status: "ok" });
        } catch {
            setError("Network error. Nothing was recorded.");
        } finally {
            setBusy(false);
        }
    }

    // Render the app while checking. A blank screen on every page load, to
    // catch a case that applies to almost nobody, is a bad trade.
    if (state.status !== "stale") return children;

    return (
        <div className="tg" role="dialog" aria-modal="true" aria-labelledby="tg-title">
            <div className="tg__panel">
                <h1 className="tg__title" id="tg-title">
                    We’ve updated our terms
                </h1>
                <p className="tg__body">
                    Our Terms of Service and Privacy Policy changed since you last
                    agreed to them. Please have a look and confirm to carry on.
                </p>

                <div className="tg__links">
                    <Link to="/terms" target="_blank" rel="noopener noreferrer">
                        Read the Terms of Service
                    </Link>
                    <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                        Read the Privacy Policy
                    </Link>
                </div>

                {error && <p className="tg__error">{error}</p>}

                <div className="tg__actions">
                    <button
                        type="button"
                        className="wb-btn wb-btn--primary wb-btn--lg"
                        onClick={accept}
                        disabled={busy}
                    >
                        {busy ? "Saving…" : "I agree"}
                    </button>
                </div>

                {/* No "decline" button. There is no account-closure flow to
                    send someone to, and a button that only closes the dialog
                    would let the prompt be dismissed without an answer -- which
                    looks like a decision and records none. Signing out is
                    always available in the nav behind this. */}
                <p className="tg__hint">Version {state.version}.</p>
            </div>
        </div>
    );
}
